import type { OptionCardView, RegisterSlot } from '../api.js';
import { slotCard } from '../api.js';
import { CARD_LABEL, OPTION_SLOT_NAME } from '../cardLabels.js';

export function RegisterStrip({
  registers, current, options = [], locked = [], upcoming = [], inactive, inactiveBy,
  moveOptions = [],
}: {
  registers: (RegisterSlot | null)[];
  current: number;
  /** Turn mode's move options by register, shown with the gadgets: they
   * were put there while programming too. */
  moveOptions?: string[][];
  /** The register the robot sits out, drawn blank: its card does nothing.
   * `inactiveBy` names whose EMP did it; without one it is a pit stop. */
  inactive?: number;
  inactiveBy?: string;
  /**
   * The viewer's own cards for registers not yet revealed, drawn greyed
   * where the strip would otherwise have nothing. A replay only.
   */
  upcoming?: (RegisterSlot | null)[];
  /**
   * The Options a gadget is named from rather than shown as its id: the
   * robot's own, or the whole catalog for a replay. Defaults to none,
   * which falls back to a readable form of the id.
   */
  options?: readonly OptionCardView[];
  /**
   * Which registers are locked, marked exactly as the programming screen
   * marks them.
   *
   * The strip showed the same five registers and said nothing about this,
   * so a card the player never chose looked like one they did. It is not
   * idle information while the strip is up: nothing in the engine spares
   * a locked register, so an Abort Switch or a Conditional substitution
   * replaces its card like any other -- and the lock then holds the NEW
   * card at End of Turn. Public, like the damage that causes it.
   */
  locked?: boolean[];
}) {
  return (
    <div
      className="strip"
      role="list"
      style={{ gridTemplateColumns: `repeat(${registers.length}, var(--card-w))` }}
    >
      {registers.map((slot, i) => {
        // The priority card is what the strip shows. A register holding
        // more than one card still runs at the first card's priority.
        const ahead = slot ? null : slotCard(upcoming[i] ?? null);
        const card = slotCard(slot) ?? ahead;
        // A pair's second card, after the primary.
        const second = (slotCard(slot) ? slot : upcoming[i])?.cards[1];
        // The Option that paired the register goes with the gadgets: both
        // were put there while programming.
        const via = (slot ?? upcoming[i])?.via;
        const gadgets = [
          ...(via ? [via] : []),
          ...((slot ?? upcoming[i])?.gadgets ?? []),
          ...(moveOptions[i] ?? []),
        ];
        return (
        <div
          key={i}
          role="listitem"
          className={`strip-slot${i + 1 === current ? ' now' : ''}`
            + `${i + 1 < current ? ' done' : ''}${ahead ? ' upcoming' : ''}${i === 5 ? ' extra' : ''}`}
        >
          <span className="strip-number num">{i + 1}</span>
          {locked[i] && <span className="slot-lock">Locked</span>}
          {i + 1 === inactive ? (
            <span
              className="strip-name muted"
              title={inactiveBy ? 'Caught by an EMP: this card does nothing' : 'Pit stop: this card does nothing'}
            >
              {inactiveBy ? 'EMP' : 'Pit stop'}
            </span>
          ) : card ? (
            <>
              {/* Priority above the effect, matching the cards in hand. */}
              <span className="strip-priority num">{card.priority}</span>
              <span className="strip-name">
                {CARD_LABEL[card.type] ?? card.type}
                {/* On a line of its own: the pair does not fit on one. */}
                {second && <><br />{`+ ${CARD_LABEL[second.type] ?? second.type}`}</>}
              </span>
            </>
          ) : (
            <span className="strip-name muted">-</span>
          )}

          {/* What was loaded into this register, named exactly as the
              programming screen names it. A gadget fires BEFORE the
              register's card, so it has to be readable while the register
              is being played and not only while it is being written.
              Independent of whether the CARD is showing: a register can
              be blank here (an Abort Switch hides what has not run yet)
              and still have a gadget the player put there themselves. */}
          {gadgets.length > 0 && (
            <span className="slot-gadgets">
              {gadgets.map((id) => {
                const option = options.find((o) => o.id === id);
                const short = option && OPTION_SLOT_NAME[option.effect];
                return (
                  <span key={id} className="slot-gadget" title={short ? option.name : undefined}>
                    {short ?? option?.name ?? readableId(id)}
                  </span>
                );
              })}
            </span>
          )}
        </div>
        );
      })}
    </div>
  );
}

/**
 * A camelCase Option id as something a player can read.
 *
 * Only reached when the Option itself is gone -- traded at a chop shop,
 * or given up on destruction -- while the register that used it is still
 * on screen. Better than printing `gooDropper` at somebody.
 */
function readableId(id: string): string {
  const spaced = id.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
